import React, { useState } from 'react';
import Table from "../../../../core/common/dataTable/index";
import { API } from '../../../../common/Constants';
import useFetch from '../../../../hooks/useFetch';
import { getAPIHeaders } from '../../../../common/Utils';
import CustomModal from '../../../../core/common/customModal';
import CustomToast from '../../../../core/common/customToast';

interface Props {
  isReadOnly: boolean;
  user: any;
  onStatusToggle: (userAccountId: number, subscriptionEnrollmentId: number) => void;
  onRefresh?: () => void;
}

const UserSubscriptionDetails: React.FC<Props> = ({ user, onStatusToggle, onRefresh, isReadOnly }) => {
  const { isLoading, callFetch } = useFetch();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{
    type: 'activate' | 'cancel';
    subscriptionId: number;
    userAccountId: number;
    subscriptionName: string;
    subscriptionEnrollmentId: number;
  } | null>(null);
  const [toastSettings, setToastSettings] = useState<{
    show: boolean;
    message: string;
    heading: string;
    type: "ERROR" | "SUCCESS" | "INFO";
  }>({
    show: false,
    message: "",
    heading: "",
    type: "INFO",
  });

  const handleStatusToggle = (record:any) => {
    console.log(record);
    const action = (record?.enrollments?.find((enrollment: any) => 
        enrollment.isCurrentEnrollment)?.subscriptionStatus !== 'CANCELLED' 
          ? 'cancel' : 'activate');
    setConfirmAction({
      type: action,
      subscriptionId: record.subscriptionId,
      userAccountId: user.userAccountId,
      subscriptionName: record.subscriptionPlanName,
      subscriptionEnrollmentId: record?.enrollments?.find((enrollment: any) => enrollment.isCurrentEnrollment)?.subscriptionEnrollmentId,
    });
    setShowConfirmModal(true);
  };

  const confirmActionHandler = async () => {
    if (!confirmAction) return;

    const { type, userAccountId, subscriptionEnrollmentId } = confirmAction;
    const endpoint = type === 'activate' 
      ? API.USER_SUBSCRIPTION_ENROLL_ACTIVATE.replace(':enrollementId', subscriptionEnrollmentId?.toString()).replace(':userId', userAccountId?.toString())
      : API.USER_SUBSCRIPTION_ENROLL_DEACTIVATE.replace(':enrollementId', subscriptionEnrollmentId?.toString()).replace(':userId', userAccountId?.toString());


    try {
      await callFetch(endpoint, 'PUT', getAPIHeaders(), null, null);
      
      setToastSettings({
        show: true,
        message: `Subscription ${type === 'activate' ? 'activated' : 'cancelled'} successfully`,
        heading: "Success",
        type: "SUCCESS",
      });

      // Refresh the data
      if (onRefresh) {
        onRefresh();
      }
    } catch (err: any) {
      setToastSettings({
        show: true,
        message: err?.response?.data || `Failed to ${type} subscription`,
        heading: "Error",
        type: "ERROR",
      });
    } finally {
      setShowConfirmModal(false);
      setConfirmAction(null);
    }
  };

  const columns = [
    {
      title: "Subs ID",
      dataIndex: "subscriptionId",
      render: (text: number) => (
        <span className="fw-medium">{text}</span>
      ),
      width: "8%",
    },
    {
      title: "Plan Name",
      dataIndex: "subscriptionPlanName",
      render: (text: string, record: any) => (
        <div>
          <h6 className="fw-medium mb-1">{text}</h6>
          <p className="text-muted mb-0 small">{record.subscriptionDescription}</p>
        </div>
      ),
      width: "16%",
    },
    {
      title: "Plan Type",
      dataIndex: "planType",
      render: (text: string) => {
        const getBadgeClass = (type: string) => {
          switch (type) {
            case 'ENTERPRISE':
              return 'bg-danger';
            case 'PREMIUM':
              return 'bg-success';
            case 'BASIC':
              return 'bg-info';
            default:
              return 'bg-secondary';
          }
        };
        return (
          <span className={`badge ${getBadgeClass(text)}`}>
            {text}
          </span>
        );
      },
      width: "8%",
      align: "center" as "center",
    },
    {
      title: "Duration",
      dataIndex: "duration",
      render: (text: string, record: any) => {
        let subscriptionValidityStatus = record?.enrollments?.find((enrollment: any) => enrollment.isCurrentEnrollment)?.subscriptionValidityStatus;
        return (
          text ?
          <span className="fw-medium">{subscriptionValidityStatus === 'LIFETIME' ? 'Lifetime' : text + ' months'}</span>
          : <></>
        )
      },
      width: "8%",
      align: "center" as "center",
    },
    {
      title: "Plan Status",
      dataIndex: "planIsActive",
      render: (text: boolean, record: any) => (
        <div className="d-flex justify-content-center">
          <span className={`badge ${text ? 'bg-success' : 'bg-secondary'}`}>
            {text ? 'Active' : 'Inactive'}
          </span>
        </div>
      ),
      width: "8%",
      align: "center" as "center",
    },
    {
      title: "Price",
      dataIndex: "promotionalPrice",
      render: (text: number, record: any) => (
        <div className="text-center">
          <div className="fw-medium">
            {record.currencyCode} {text.toFixed(2)}
          </div>
          {record.subscriptionPrice > 0 && (
            <div className="text-muted small">
              <del>{record.currencyCode} {record.subscriptionPrice.toFixed(2)}</del>
            </div>
          )}
        </div>
      ),
      width: "8%",
      align: "center" as "center",
    },
    {
      title: "Enrollment Date",
      dataIndex: "",
      render: (text: string, record: any) => {
        const subscriptionEnrollmentDate = record?.enrollments?.find((enrollment: any) => enrollment.isCurrentEnrollment)?.subscriptionEnrollmentDate;
        return (
        <span className="small">
          {subscriptionEnrollmentDate ? new Date(subscriptionEnrollmentDate).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          }) : ''}
        </span>)
      },
      width: "8%",
      align: "center" as "center",
    },
    {
      title: "Start Date",
      dataIndex: "",
      render: (text: string, record: any) => {
        const subscriptionStartDate = record?.enrollments?.find((enrollment: any) => enrollment.isCurrentEnrollment)?.subscriptionEnrollmentDate;
        return (
        <span className="small">
          {subscriptionStartDate ? new Date(subscriptionStartDate).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          }) : ''}
        </span>)
      },
      width: "8%",
      align: "center" as "center",
    },
    {
      title: "Expiry Date",
      dataIndex: "",
      render: (text: string, record: any) => {
        let expiryDate = record?.enrollments?.find((enrollment: any) => enrollment.isCurrentEnrollment)?.subscriptionExpiresDate;
        return (
        <span className="small">
          {expiryDate ? new Date(expiryDate).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          }) : 'Lifetime'}
        </span>)
      },
      width: "8%",
      align: "center" as "center",
    },
    {
      title: "Enrollment Status",
      dataIndex: "",
      render: (text: string, record: any) => {
        const getBadgeClass = (status: string) => {
          switch (status) {
            case 'ACTIVE':
              return 'bg-success';
            case 'PENDING':
              return 'bg-warning';
            case 'CANCELLED':
              return 'bg-danger';
            case 'EXPIRED':
              return 'bg-secondary';
            default:
              return 'bg-secondary';
          }
        };
        return (
          <span className={`badge ${getBadgeClass(record.enrollments?.find((enrollment: any) => enrollment.isCurrentEnrollment)?.subscriptionStatus)}`}>
            {record.enrollments?.find((enrollment: any) => enrollment.isCurrentEnrollment)?.subscriptionStatus}
          </span>
        );
      },
      width: "8%",
      align: "center" as "center",
    },
    {
      title: "Validity Status",
      dataIndex: "subscriptionValidityStatus",
      render: (text: string, record: any) => {
        const subscriptionValidityStatus = record?.enrollments?.find((enrollment: any) => enrollment.isCurrentEnrollment)?.subscriptionValidityStatus;
        const getValidityBadgeClass = (status: string) => {
          switch (status) {
            case 'ACTIVE':
              return 'bg-success';
            case 'LIFETIME':
              return 'bg-info';
            case 'EXPIRED':
              return 'bg-danger';
            case 'PENDING':
              return 'bg-warning';
            default:
              return 'bg-secondary';
          }
        };
        return (
          <div className="d-flex justify-content-center">
            <span className={`badge ${getValidityBadgeClass(subscriptionValidityStatus)}`}>
              {subscriptionValidityStatus}
            </span>
          </div>
        );
      },
      width: "8%",
      align: "center" as "center",
    },
    {
      title: "Actions",
      dataIndex: "subscriptionId",
      render: (subscriptionId: number, record: any) => {
        const subscriptionStatus = record?.enrollments?.find((enrollment: any) => enrollment.isCurrentEnrollment)?.subscriptionStatus;
        return (
        <div className="d-flex justify-content-center">
          {!isReadOnly && <button
            disabled={isReadOnly || isLoading}
            className={`btn btn-sm ${subscriptionStatus !== 'CANCELLED' ? 'btn-outline-danger' : 'btn-outline-success'}`}
            onClick={() => handleStatusToggle(record)}
            title={subscriptionStatus !== 'CANCELLED' ? 'Cancel Subscription' : 'Activate Subscription'}
          >
            <i className={`ti ${subscriptionStatus !== 'CANCELLED' ? 'ti-x' : 'ti-check'} me-1`} />
            {subscriptionStatus !== 'CANCELLED' ? 'Cancel' : 'Activate'}
          </button>}
        </div>)
      },
      width: "12%",
      align: "center" as "center",
      hidden: isReadOnly,
    },
  ];

  return (
    <div>
      <CustomToast
        show={toastSettings?.show}
        message={toastSettings?.message}
        heading={toastSettings?.heading}
        type={toastSettings?.type}
        onClose={() => {
          setToastSettings((prev) => ({
            ...prev,
            show: false,
          }));
        }}
      />

      {/* Subscriptions Table */}
      <div className="card">
        <div className="card-body p-0">
          <Table
            dataSource={user.subscriptions}
            columns={columns}
            Selection={false}
          />
        </div>
      </div>

      {/* Confirmation Modal */}
      <CustomModal
        show={showConfirmModal}
        title={`Confirm Subscription ${confirmAction?.type === 'activate' ? 'Activation' : 'Cancellation'}`}
        onClose={() => {
          setShowConfirmModal(false);
          setConfirmAction(null);
        }}

        bodyContent={
          <div className="text-center">
            <div className="mb-3">
              <i className={`ti ${confirmAction?.type === 'activate' ? 'ti-check-circle' : 'ti-alert-circle'} fs-1 ${confirmAction?.type === 'activate' ? 'text-success' : 'text-warning'}`}></i>
            </div>
            <h5 className="mb-3">
              {confirmAction?.type === 'activate' ? 'Activate' : 'Cancel'} Subscription
            </h5>
            <p className="text-muted mb-4">
              Are you sure you want to {confirmAction?.type === 'activate' ? 'activate' : 'cancel'} the subscription 
              <strong> "{confirmAction?.subscriptionName}"</strong> for user <strong>"{user.userFullName}"</strong>?
            </p>
            <div className="d-flex justify-content-center gap-2">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowConfirmModal(false);
                  setConfirmAction(null);
                }}
              >
                Cancel
              </button>
              <button
                className={`btn ${confirmAction?.type === 'activate' ? 'btn-success' : 'btn-danger'}`}
                onClick={confirmActionHandler}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Processing...
                  </>
                ) : (
                  `Yes, ${confirmAction?.type === 'activate' ? 'Activate' : 'Cancel'}`
                )}
              </button>
            </div>
          </div>
        }
      />
    </div>
  );
};

export default UserSubscriptionDetails;
